import { ProfileChannel, ProfilePublishValues, ProfileReleaseStage } from "@/types/profile";
import {
  CHANNEL_DISPLAY_LABELS,
  profilePublishingTypesAllowAutoRelease,
  PROFILE_PUBLISH_DOWNLOAD_CHILD,
  PROFILE_PUBLISH_JOB_TYPES,
  PROFILE_RELEASE_MODE_LABELS,
  PROFILE_RELEASE_REQUIRED_STAGES,
  PROFILE_RELEASE_STAGES,
  ProfilePublishDownloadChildId,
  ProfileReleaseMode,
  ProfileReleaseStageName
} from "@/constants/profileConstants";

const STAGE_KEY: Record<ProfileReleaseStageName, "Draft" | "Preview" | "Live"> = {
  Draft: "Draft",
  Preview: "Preview",
  Live: "Live"
};

const normalizeList = (values: ProfilePublishValues | undefined): string[] => {
  if (!values) return [];
  if (Array.isArray(values)) return values.map(String);
  return Object.keys(values);
};

const listToPublishValues = (items: string[]): ProfilePublishValues =>
  items.length > 0 ? items : {};

const jobTypeKeys = (values: ProfilePublishValues | undefined): string[] => {
  if (!values) return [];
  if (Array.isArray(values)) return values.map(String);
  return Object.keys(values);
};

export const getChannelDisplayLabel = (channelName: string): string => {
  const key = channelName.toLowerCase();
  return CHANNEL_DISPLAY_LABELS[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
};

const LEGACY_PUBLISH_JOB_TYPE_KEYS = ["df", "dg"] as const;

const getPublishJobTypeRecord = (channel: ProfileChannel): Record<string, unknown> => {
  const jobType = channel.Publish?.JobType;
  if (!jobType || Array.isArray(jobType)) {
    if (Array.isArray(jobType)) {
      return Object.fromEntries(jobType.map((key) => [String(key), null]));
    }
    return {};
  }
  return { ...jobType };
};

const isPreBuiltDbEntry = (entry: unknown): boolean =>
  typeof entry === "object" && entry !== null && (entry as { PreBuilt?: boolean }).PreBuilt === true;

/** Every `Publish.JobType` entry exactly as stored, in profile order, with db's `PreBuilt` flag. */
export const getPublishJobTypeEntries = (channel: ProfileChannel): Array<{ jobType: string; preBuilt: boolean }> =>
  Object.entries(getPublishJobTypeRecord(channel)).map(([jobType, entry]) => ({
    jobType,
    preBuilt: jobType.toLowerCase() === PROFILE_PUBLISH_JOB_TYPES.CREATE_UPLOAD && isPreBuiltDbEntry(entry)
  }));

// Inline rather than isDevelopmentChannel (jobs/profileUtils) — that module imports profileFormUtils, which imports this one.
const isDevelopmentProfileChannel = (channel: ProfileChannel): boolean => channel.Name?.toLowerCase() === "development";

/** `auto` release mode is only valid on allowed channels and publishing types. */
export const isAutoReleaseModeAllowed = (
  channel: ProfileChannel,
  releaseTypes: string[] = []
): boolean => {
  if (!profilePublishingTypesAllowAutoRelease(releaseTypes)) return false;

  return isDevelopmentProfileChannel(channel);
};

/** Coerces `auto` to `manual` when the channel or profile does not support auto release. */
export const clampChannelReleaseMode = (
  channel: ProfileChannel,
  releaseTypes: string[] = []
): ProfileChannel => {
  if (getChannelReleaseMode(channel) === "auto" && !isAutoReleaseModeAllowed(channel, releaseTypes)) {
    return setChannelReleaseMode(channel, "manual", releaseTypes);
  }
  if (isPrdEnvironmentDisabled(channel)) {
    return setEnvironmentSelected(channel, "prd", false);
  }
  return channel;
};

/** Resolves `ReleaseMode` from API YAML, with fallbacks for legacy `Releasable` values. */
export const getChannelReleaseMode = (channel: ProfileChannel): ProfileReleaseMode => {
  const raw = channel.ReleaseMode ?? channel.Releasable;
  if (raw === true || raw === "manual") return "manual";
  if (raw === false || raw === "disabled") return "disabled";
  if (raw === "auto" || raw === "fast") return "auto";
  return "manual";
};

export const hasChannelReleaseMode = (channel: ProfileChannel): boolean =>
  channel.ReleaseMode !== undefined || channel.Releasable !== undefined;

export const formatChannelReleaseMode = (channel: ProfileChannel): string => {
  if (!hasChannelReleaseMode(channel)) return "-";
  return PROFILE_RELEASE_MODE_LABELS[getChannelReleaseMode(channel)];
};

/** Normalizes legacy `Releasable` values to `ReleaseMode` for dashboard/review display. */
export const normalizeProfileChannelForDisplay = (channel: ProfileChannel): ProfileChannel => {
  if (!hasChannelReleaseMode(channel)) return channel;
  const { Releasable: _legacy, ...rest } = channel;
  return { ...rest, ReleaseMode: getChannelReleaseMode(channel) };
};

export const isReleaseModeDisabled = (channel: ProfileChannel): boolean =>
  getChannelReleaseMode(channel) === "disabled";

export const requiresReleaseDetails = (channel: ProfileChannel): boolean => !isReleaseModeDisabled(channel);

/** PRD is off for `auto` release mode and for the development channel. */
export const isPrdEnvironmentDisabled = (channel: ProfileChannel): boolean =>
  getChannelReleaseMode(channel) === "auto" || isDevelopmentProfileChannel(channel);

export const isReleaseDetailsDisabled = (channel: ProfileChannel): boolean => isReleaseModeDisabled(channel);

export const getPublishDownloadChildSelection = (channel: ProfileChannel): ProfilePublishDownloadChildId | null => {
  const record = getPublishJobTypeRecord(channel);

  if (PROFILE_PUBLISH_JOB_TYPES.INGEST_UPLOAD in record) {
    return PROFILE_PUBLISH_DOWNLOAD_CHILD.INGEST_UPLOAD;
  }

  if (PROFILE_PUBLISH_JOB_TYPES.CREATE_UPLOAD in record) {
    return isPreBuiltDbEntry(record[PROFILE_PUBLISH_JOB_TYPES.CREATE_UPLOAD])
      ? PROFILE_PUBLISH_DOWNLOAD_CHILD.UPLOAD_PREBUILT
      : PROFILE_PUBLISH_DOWNLOAD_CHILD.CREATE_UPLOAD;
  }

  if ("dg" in record) {
    return PROFILE_PUBLISH_DOWNLOAD_CHILD.CREATE_UPLOAD;
  }

  return null;
};

export const isDownloadImagePublishSelected = (channel: ProfileChannel): boolean =>
  getPublishDownloadChildSelection(channel) !== null;

export const isPublishInstallSelected = (channel: ProfileChannel): boolean => {
  const record = getPublishJobTypeRecord(channel);
  return (
    PROFILE_PUBLISH_JOB_TYPES.INSTALL in record &&
    !(PROFILE_PUBLISH_JOB_TYPES.INGEST_UPLOAD in record)
  );
};

export const isPublishInstallDisabled = (channel: ProfileChannel): boolean =>
  getPublishDownloadChildSelection(channel) === PROFILE_PUBLISH_DOWNLOAD_CHILD.INGEST_UPLOAD;

export const isPublishIngestUploadDisabled = (channel: ProfileChannel): boolean =>
  isPublishInstallSelected(channel);

const normalizePublishJobTypeRecord = (record: Record<string, unknown>): Record<string, unknown> => {
  const next = { ...record };
  LEGACY_PUBLISH_JOB_TYPE_KEYS.forEach((key) => delete next[key]);
  if ("dg" in record && !(PROFILE_PUBLISH_JOB_TYPES.CREATE_UPLOAD in next)) {
    next[PROFILE_PUBLISH_JOB_TYPES.CREATE_UPLOAD] = null;
  }
  return next;
};

const buildPublishJobTypePayload = (record: Record<string, unknown>): ProfilePublishValues => {
  const normalized = normalizePublishJobTypeRecord(record);
  const next: Record<string, unknown> = {};

  if (
    PROFILE_PUBLISH_JOB_TYPES.INSTALL in normalized &&
    !(PROFILE_PUBLISH_JOB_TYPES.INGEST_UPLOAD in normalized)
  ) {
    next[PROFILE_PUBLISH_JOB_TYPES.INSTALL] = null;
  }

  if (PROFILE_PUBLISH_JOB_TYPES.INGEST_UPLOAD in normalized) {
    next[PROFILE_PUBLISH_JOB_TYPES.INGEST_UPLOAD] = null;
  } else if (PROFILE_PUBLISH_JOB_TYPES.CREATE_UPLOAD in normalized) {
    const dbEntry = normalized[PROFILE_PUBLISH_JOB_TYPES.CREATE_UPLOAD];
    next[PROFILE_PUBLISH_JOB_TYPES.CREATE_UPLOAD] = isPreBuiltDbEntry(dbEntry) ? { PreBuilt: true } : null;
  }

  return next;
};

export const getNormalizedPublishJobTypes = (channel: ProfileChannel): ProfilePublishValues =>
  buildPublishJobTypePayload(getPublishJobTypeRecord(channel));

const patchPublishJobTypes = (
  channel: ProfileChannel,
  mutate: (record: Record<string, unknown>) => void
): ProfileChannel => {
  const record = getPublishJobTypeRecord(channel);
  LEGACY_PUBLISH_JOB_TYPE_KEYS.forEach((key) => delete record[key]);

  mutate(record);

  if (record[PROFILE_PUBLISH_JOB_TYPES.INGEST_UPLOAD] !== undefined) {
    delete record[PROFILE_PUBLISH_JOB_TYPES.INSTALL];
    delete record[PROFILE_PUBLISH_JOB_TYPES.CREATE_UPLOAD];
  }

  if (record[PROFILE_PUBLISH_JOB_TYPES.INSTALL] !== undefined) {
    delete record[PROFILE_PUBLISH_JOB_TYPES.INGEST_UPLOAD];
  }

  return pruneReleaseImagesToPublishSelection({
    ...channel,
    Publish: {
      ...channel.Publish,
      JobType: buildPublishJobTypePayload(record)
    }
  });
};

export const setPublishInstallSelected = (channel: ProfileChannel, selected: boolean): ProfileChannel =>
  patchPublishJobTypes(channel, (record) => {
    if (selected) {
      record[PROFILE_PUBLISH_JOB_TYPES.INSTALL] = null;
      delete record[PROFILE_PUBLISH_JOB_TYPES.INGEST_UPLOAD];
    } else {
      delete record[PROFILE_PUBLISH_JOB_TYPES.INSTALL];
    }
  });

export const setDownloadImagePublishSelected = (channel: ProfileChannel, selected: boolean): ProfileChannel => {
  if (selected && !isDownloadImagePublishSelected(channel)) {
    return setPublishDownloadChild(channel, PROFILE_PUBLISH_DOWNLOAD_CHILD.CREATE_UPLOAD);
  }

  return patchPublishJobTypes(channel, (record) => {
    delete record[PROFILE_PUBLISH_JOB_TYPES.CREATE_UPLOAD];
    delete record[PROFILE_PUBLISH_JOB_TYPES.INGEST_UPLOAD];
  });
};

export const setPublishDownloadChild = (
  channel: ProfileChannel,
  childId: ProfilePublishDownloadChildId
): ProfileChannel =>
  patchPublishJobTypes(channel, (record) => {
    delete record[PROFILE_PUBLISH_JOB_TYPES.CREATE_UPLOAD];
    delete record[PROFILE_PUBLISH_JOB_TYPES.INGEST_UPLOAD];

    if (childId === PROFILE_PUBLISH_DOWNLOAD_CHILD.INGEST_UPLOAD) {
      record[PROFILE_PUBLISH_JOB_TYPES.INGEST_UPLOAD] = null;
      delete record[PROFILE_PUBLISH_JOB_TYPES.INSTALL];
      return;
    }

    record[PROFILE_PUBLISH_JOB_TYPES.CREATE_UPLOAD] =
      childId === PROFILE_PUBLISH_DOWNLOAD_CHILD.UPLOAD_PREBUILT ? { PreBuilt: true } : null;
  });

export const getSelectedPublishJobTypeKeys = (channel: ProfileChannel): string[] =>
  jobTypeKeys(buildPublishJobTypePayload(getPublishJobTypeRecord(channel)) as ProfilePublishValues);

/** Job types (di, db, ds) that may be chosen as Allowed Images — mirrors Images to Publish. */
export const getReleaseSelectableJobTypes = (channel: ProfileChannel): string[] =>
  getSelectedPublishJobTypeKeys(channel);

export const isReleaseImageJobTypeSelectable = (channel: ProfileChannel, jobType: string): boolean =>
  getReleaseSelectableJobTypes(channel).includes(jobType);

/** Job types that may be chosen for Preview/Live required images — mirrors Allowed Images (Preview optional, Live required via validation). */
export const getRequiredImageSelectableJobTypes = (
  channel: ProfileChannel,
  stage: ProfileReleaseStageName
): string[] => {
  if (stage === "Preview" || stage === "Live") {
    return getReleaseAllowedImageSelection(channel);
  }
  return [];
};

export const isRequiredImageJobTypeSelectable = (
  channel: ProfileChannel,
  stage: ProfileReleaseStageName,
  jobType: string
): boolean => getRequiredImageSelectableJobTypes(channel, stage).includes(jobType);

/** @deprecated Use `isPublishInstallSelected` / download child helpers for the submit form. */
export const isPublishJobTypeSelected = (channel: ProfileChannel, jobType: string): boolean =>
  getSelectedPublishJobTypeKeys(channel).includes(jobType);

/** Sets `ReleaseMode` and clears PRD when release is disabled or auto. */
export const setChannelReleaseMode = (
  channel: ProfileChannel,
  releaseMode: ProfileReleaseMode,
  releaseTypes: string[] = []
): ProfileChannel => {
  const resolvedMode =
    releaseMode === "auto" && !isAutoReleaseModeAllowed(channel, releaseTypes) ? "manual" : releaseMode;
  const { Releasable: _legacy, ...rest } = channel;
  const next: ProfileChannel = { ...rest, ReleaseMode: resolvedMode };
  if (resolvedMode === "disabled" || resolvedMode === "auto") {
    return setEnvironmentSelected(next, "prd", false);
  }
  return next;
};

export const getSelectedEnvironments = (channel: ProfileChannel): string[] =>
  normalizeList(channel.Publish?.Environment).map((e) => e.toLowerCase());

export const setEnvironmentSelected = (
  channel: ProfileChannel,
  env: string,
  selected: boolean
): ProfileChannel => {
  const envKey = env.toLowerCase();
  const current = new Set(getSelectedEnvironments(channel));
  if (selected) current.add(envKey);
  else current.delete(envKey);
  return {
    ...channel,
    Publish: {
      ...channel.Publish,
      Environment: listToPublishValues(Array.from(current))
    }
  };
};

const getReleaseStage = (channel: ProfileChannel, stage: ProfileReleaseStageName): ProfileReleaseStage => {
  const key = STAGE_KEY[stage];
  return channel.Release?.[key] ?? {};
};

const patchReleaseStage = (
  channel: ProfileChannel,
  stage: ProfileReleaseStageName,
  patch: Partial<ProfileReleaseStage>
): ProfileChannel => {
  const key = STAGE_KEY[stage];
  return {
    ...channel,
    Release: {
      ...channel.Release,
      [key]: { ...getReleaseStage(channel, stage), ...patch }
    }
  };
};

/** Release-level allowed images (legacy profiles may store these under a release stage). */
export const getReleaseAllowedImageSelection = (channel: ProfileChannel): string[] => {
  const atRelease = normalizeList(channel.Release?.AllowedImages);
  if (atRelease.length > 0) return atRelease;

  const legacy = new Set<string>();
  PROFILE_RELEASE_STAGES.forEach((stage) => {
    normalizeList(getReleaseStage(channel, stage).AllowedImages).forEach((jobType) => legacy.add(jobType));
  });
  return Array.from(legacy);
};

/** Keeps allowed/required release images aligned with publish → allowed; Preview/Live required ⊆ allowed. */
export const pruneReleaseImageCascade = (channel: ProfileChannel): ProfileChannel => {
  const publishSelectable = new Set(getReleaseSelectableJobTypes(channel));

  let next: ProfileChannel = {
    ...channel,
    Release: {
      ...channel.Release,
      AllowedImages: listToPublishValues(
        getReleaseAllowedImageSelection(channel).filter((jobType) => publishSelectable.has(jobType))
      )
    }
  };

  const allowedSelectable = new Set(getReleaseAllowedImageSelection(next));

  const previewRequired = getStageImageSelection(next, "Preview", "RequiredImages");
  const filteredPreview = previewRequired.filter((jobType) => allowedSelectable.has(jobType));
  if (filteredPreview.length !== previewRequired.length) {
    next = patchReleaseStage(next, "Preview", {
      RequiredImages: buildRequiredImagesRecord(next, "Preview", new Set(filteredPreview))
    });
  }

  const liveRequired = getStageImageSelection(next, "Live", "RequiredImages");
  const filteredLive = liveRequired.filter((jobType) => allowedSelectable.has(jobType));
  if (filteredLive.length !== liveRequired.length) {
    next = patchReleaseStage(next, "Live", {
      RequiredImages: buildRequiredImagesRecord(next, "Live", new Set(filteredLive))
    });
  }

  PROFILE_RELEASE_STAGES.forEach((stage) => {
    if ((PROFILE_RELEASE_REQUIRED_STAGES as readonly string[]).includes(stage)) return;

    const currentRequired = getStageImageSelection(next, stage, "RequiredImages");
    const filteredRequired = currentRequired.filter((jobType) => publishSelectable.has(jobType));
    if (filteredRequired.length === currentRequired.length) return;

    next = patchReleaseStage(next, stage, {
      RequiredImages: buildRequiredImagesRecord(next, stage, new Set(filteredRequired))
    });
  });

  return next;
};

/** @deprecated Prefer `pruneReleaseImageCascade`. */
export const pruneReleaseImagesToPublishSelection = (channel: ProfileChannel): ProfileChannel =>
  pruneReleaseImageCascade(channel);

export const setReleaseAllowedImageSelected = (
  channel: ProfileChannel,
  jobType: string,
  selected: boolean
): ProfileChannel => {
  if (selected && !isReleaseImageJobTypeSelectable(channel, jobType)) {
    return channel;
  }

  const current = new Set(getReleaseAllowedImageSelection(channel));
  if (selected) current.add(jobType);
  else current.delete(jobType);

  return pruneReleaseImageCascade({
    ...channel,
    Release: {
      ...channel.Release,
      AllowedImages: listToPublishValues(Array.from(current))
    }
  });
};

export const getStageImageSelection = (
  channel: ProfileChannel,
  stage: ProfileReleaseStageName,
  field: "RequiredImages"
): string[] => normalizeList(getReleaseStage(channel, stage)[field]);

const buildRequiredImagesRecord = (
  channel: ProfileChannel,
  stage: ProfileReleaseStageName,
  selectedTypes: Set<string>
): Record<string, unknown> => {
  const existing = getReleaseStage(channel, stage).RequiredImages;
  const asRecord: Record<string, unknown> =
    existing && !Array.isArray(existing) ? { ...existing } : {};

  Object.keys(asRecord).forEach((key) => {
    if (!selectedTypes.has(key)) {
      delete asRecord[key];
    }
  });

  selectedTypes.forEach((type) => {
    if (!(type in asRecord)) {
      asRecord[type] = null;
    }
  });

  return asRecord;
};

export const setStageImageSelected = (
  channel: ProfileChannel,
  stage: ProfileReleaseStageName,
  jobType: string,
  selected: boolean
): ProfileChannel => {
  if (selected && !isRequiredImageJobTypeSelectable(channel, stage, jobType)) {
    return channel;
  }

  const current = new Set(getStageImageSelection(channel, stage, "RequiredImages"));
  if (selected) current.add(jobType);
  else current.delete(jobType);

  return pruneReleaseImageCascade(
    patchReleaseStage(channel, stage, {
      RequiredImages: buildRequiredImagesRecord(channel, stage, current)
    })
  );
};

type RequiredImageEventEntry = {
  SendEvent?: boolean;
  Event?: { Enabled?: boolean };
};

/** Backend treats missing Event as enabled; only `Event.Enabled: false` disables sync event. */
export const isRequiredImageSendEventDisabled = (entry: unknown): boolean => {
  if (entry === null || entry === undefined) return false;
  if (typeof entry !== "object") return false;
  const value = entry as RequiredImageEventEntry;
  if (value.SendEvent === false) return true;
  return value.Event?.Enabled === false;
};

/** Sync Event toggles on Preview/Live required images (default on when required). */
export const getRequiredImageSendEvent = (
  channel: ProfileChannel,
  stage: ProfileReleaseStageName,
  jobType: string
): boolean => {
  const required = getReleaseStage(channel, stage).RequiredImages;
  if (!required || Array.isArray(required)) return true;
  return !isRequiredImageSendEventDisabled(required[jobType]);
};

export const setRequiredImageSendEvent = (
  channel: ProfileChannel,
  stage: ProfileReleaseStageName,
  jobType: string,
  sendEventEnabled: boolean
): ProfileChannel => {
  const stageData = getReleaseStage(channel, stage);
  const required = stageData.RequiredImages;
  const asRecord: Record<string, unknown> =
    required && !Array.isArray(required) ? { ...required } : {};

  const selectedTypes = getStageImageSelection(channel, stage, "RequiredImages");
  selectedTypes.forEach((type) => {
    if (!(type in asRecord)) asRecord[type] = null;
  });

  if (sendEventEnabled) {
    asRecord[jobType] = null;
  } else {
    asRecord[jobType] = { Event: { Enabled: false } };
  }

  return patchReleaseStage(channel, stage, { RequiredImages: asRecord });
};
